// Isometric drawing helpers shared by the landing hero and the status page illustrations.
// World (x, y) lies on the ground plane and z points up; screen points are SVG user units.

export type Point = [number, number];

export const ISO_COS = Math.cos(Math.PI / 6);

export const points = (...list: Point[]) =>
  list.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" ");

export const convexHull = (input: Point[]): Point[] => {
  const sorted = [...input].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const cross = (o: Point, a: Point, b: Point) =>
    (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const half = (list: Point[]) => {
    const out: Point[] = [];
    for (const p of list) {
      while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], p) <= 0) out.pop();
      out.push(p);
    }
    out.pop();
    return out;
  };
  return [...half(sorted), ...half([...sorted].reverse())];
};

/** Polyline through screen points with softened corners. */
export const roundedPath = (list: Point[], radius: number) => {
  let d = `M${list[0][0].toFixed(1)} ${list[0][1].toFixed(1)}`;
  for (let index = 1; index < list.length - 1; index++) {
    const [px, py] = list[index];
    const toward = (other: Point) => {
      const length = Math.hypot(other[0] - px, other[1] - py);
      const r = Math.min(radius, length / 2);
      return [px + ((other[0] - px) / length) * r, py + ((other[1] - py) / length) * r];
    };
    const [ax, ay] = toward(list[index - 1]);
    const [bx, by] = toward(list[index + 1]);
    d += ` L${ax.toFixed(1)} ${ay.toFixed(1)} Q${px.toFixed(1)} ${py.toFixed(1)} ${bx.toFixed(1)} ${by.toFixed(1)}`;
  }
  const last = list[list.length - 1];
  return `${d} L${last[0].toFixed(1)} ${last[1].toFixed(1)}`;
};

/** A rounded rectangle on the ground plane, as a polygon. */
export const roundedFootprint = (
  cx: number,
  cy: number,
  hx: number,
  hy: number,
  radius: number,
) => {
  const out: Point[] = [];
  const corners: [number, number, number][] = [
    [cx + hx - radius, cy + hy - radius, 0],
    [cx - hx + radius, cy + hy - radius, 90],
    [cx - hx + radius, cy - hy + radius, 180],
    [cx + hx - radius, cy - hy + radius, 270],
  ];
  for (const [x, y, start] of corners) {
    for (let step = 0; step <= 4; step++) {
      const angle = ((start + step * 22.5) * Math.PI) / 180;
      out.push([x + Math.cos(angle) * radius, y + Math.sin(angle) * radius]);
    }
  }
  return out;
};

export const circleFootprint = (cx: number, cy: number, radius: number): Point[] =>
  Array.from({ length: 36 }, (_, index) => {
    const angle = (index / 36) * Math.PI * 2;
    return [cx + Math.cos(angle) * radius, cy + Math.sin(angle) * radius];
  });

/** Projection and shape helpers for a scene whose world origin sits at the given screen point. */
export const createIsometric = (originX: number, originY: number) => {
  const iso = (x: number, y: number, z = 0): Point => [
    originX + (x - y) * ISO_COS,
    originY + (x + y) * 0.5 - z,
  ];

  /** Maps flat 2D drawing coordinates onto the top face at a world point. */
  const topFace = (x: number, y: number, z: number) => {
    const [sx, sy] = iso(x, y, z);
    return `matrix(${ISO_COS} 0.5 ${-ISO_COS} 0.5 ${sx.toFixed(1)} ${sy.toFixed(1)})`;
  };

  /** A rounded box: its top face, and the silhouette of its sides (shaded by a gradient). */
  const roundedBox = (
    cx: number,
    cy: number,
    hx: number,
    hy: number,
    z0: number,
    z1: number,
    radius: number,
  ) => {
    const footprint = roundedFootprint(cx, cy, hx, hy, radius);
    const top = footprint.map(([x, y]) => iso(x, y, z1));
    const bottom = footprint.map(([x, y]) => iso(x, y, z0));
    return { footprint, top: points(...top), side: points(...convexHull([...top, ...bottom])) };
  };

  const ellipse = (cx: number, cy: number, radius: number, z: number) => {
    const [x, y] = iso(cx, cy, z);
    return { cx: x, cy: y, rx: radius * Math.SQRT2 * ISO_COS, ry: radius * Math.SQRT1_2 };
  };

  return { iso, topFace, roundedBox, ellipse };
};
